import React, { useState, useCallback, useEffect} from "react";
import useFormCache from "../hooks/useFormCache";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css"
import {
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Radio,
  RadioGroup,
  Checkbox,
  Select,
  MenuItem,
  TextField,
  Button,
  Box,
  Typography,
} from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import AnnouncementPreview from "./ChangeAnnouncementPreview";
import { sendEmail } from "../apis/SendEmailsApi";
import { ShowCustomAlert } from "./showCustomAlert";
import { fetchBusinessSolutionNames, fetchSolutionDataByName } from "../apis/fetchBusinessSolutionApi";
import { useAuth } from "../middleware/AuthContext";

interface ChangeFormData extends Record<string, unknown> {
  customerImpact: string;
  hotfix: string;
  businessSolution: string;
  affectedBrands: string[];
  modGenerations: string[];
  regions: string[];
  instances: string;
  downtimeImpact: string;
  developerActions: string;
  consumerActions: string;
  ticketLink: string;
  changeNotes: string;
  startDeployment: string;
  endDeployment: string;
  affectedServices: string[];
  businessAffected: string[];
  affectedApplications: string[];
  contactEmail: string;
  slackName: string;
  slackLink: string;
  eventType: string;
  status: string;
  gmtOffset: string;
}

const AnnouncementForm: React.FC<{ finalDecision?: string | null }> = () => {
  const { csrfToken } = useAuth();
  const [formData, setFormData, , cacheControls] = useFormCache<ChangeFormData>(
    "change-form-cache",
    {
      customerImpact: "",
      hotfix: "",
      businessSolution: "",
      affectedBrands: [],
      modGenerations: [],
      regions: [],
      instances: "",
      downtimeImpact: "",
      developerActions: "",
      consumerActions: "",
      ticketLink: "",
      changeNotes: "",
      startDeployment: "",
      endDeployment: "",
      affectedServices: [],
      businessAffected: [],
      affectedApplications: [],
      contactEmail: "",
      slackName: "",
      slackLink: "",
      eventType: "Standard Change",
      status: "",
      gmtOffset: "",
    },
    ["gmtOffset"] // gmtOffset is computed dynamically – don't persist it
  );

  const [showCachePrompt, setShowCachePrompt] = useState(cacheControls.hasCachedData);
  const [dynamicAffectedServices, setDynamicAffectedServices] = useState<string[]>([]);
  const [dynamicApplications, setDynamicApplications] = useState<string[]>([]);
  const [businessSolutions, setBusinessSolutions] = useState<string[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [activeStatus, setActiveStatus] = useState<string | null>(null);
  const [businessSolutionServices, setBusinessSolutionServices] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState(false);
  const [berlinTime, setBerlinTime] = useState('');
  const [gmtOffset, setGmtOffset] = useState('');

  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const berlinTimeFormatted = new Intl.DateTimeFormat("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
        timeZone: "Europe/Berlin",
      }).format(now);

      const withTimeZone = now.toLocaleString("en-US", {
        timeZone: "Europe/Berlin",
        timeZoneName: "short",
      });

      const match = withTimeZone.match(/GMT([+-]\d+)/);
      const offset = match ? `GMT${match[1]}` : 'undefined';

      setBerlinTime(berlinTimeFormatted);
      setGmtOffset(offset);
      setFormData((prev) => ({ ...prev, gmtOffset: offset }));
    };

    updateTime();
    const interval = setInterval(updateTime, 60000); // Update every minute

    // Fetch business solutions and prefetch services only once on mount
    const fetchAllSolutions = async () => {
      try {
        const solutions = await fetchBusinessSolutionNames("change");
        setBusinessSolutions(solutions);

        // Pre-fetch services for all solutions
        const servicesMap: Record<string, string[]> = {};
        for (const solution of solutions) {
          const response = await fetchSolutionDataByName(solution, csrfToken);
          servicesMap[solution] = response?.services ?? [];
        }
        setBusinessSolutionServices(servicesMap);
      } catch (error) {
        console.error("Failed to fetch business solutions:", error);
      }
    };
    fetchAllSolutions();

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [csrfToken]);

  const handleStatusChange = (status: string) => {
    setActiveStatus(status);
    setFormData((prev) => ({ ...prev, status }));
  };

  const fetchAffectedServices = useCallback(async (businessSolution: string) => {
    if (!businessSolution) {
      setDynamicAffectedServices([]);
      setFormData((prev) => ({
        ...prev,
        contactEmail: "",
        slackName: "",
        slackLink: "",
      }));
      return;
    }

    try {
      const response = await fetchSolutionDataByName(businessSolution, csrfToken);
      setDynamicAffectedServices(response?.services ?? []);
      setBusinessSolutionServices(prev => ({
        ...prev,
        [businessSolution]: response?.services ?? [],
      }));

      setFormData((prev) => ({
        ...prev,
        contactEmail: (response as { email?: string })?.email ?? "",
        slackName: (response as { slackName?: string })?.slackName ?? "",
        slackLink: (response as { slackLink?: string })?.slackLink ?? "",
      }));
    } catch (error) {
      console.error("Error fetching affected services:", error);
      setDynamicAffectedServices([]);
      setFormData((prev) => ({
        ...prev,
        contactEmail: "",
        slackName: "",
        slackLink: "",
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [csrfToken]);

  // Dedicated handler for the MUI Select (businessSolution dropdown)
  const handleBusinessSolutionChange = useCallback((event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    fetchAffectedServices(value);
    setFormData((prev) => ({
      ...prev,
      businessSolution: value,
      affectedServices: [],
    }));
  }, [fetchAffectedServices, setFormData]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target as { name: keyof ChangeFormData; value: string };

      // Handle businessAffected change
      if (name === "businessAffected" && e.target instanceof HTMLInputElement && e.target.type === "checkbox") {
        const checked = e.target.checked;
        
        setFormData((prev) => {
          const updatedBusinessAffected = checked
            ? [...prev.businessAffected, value]
            : prev.businessAffected.filter((item) => item !== value);

          // Update applications based on selected business solutions
          const applications = updatedBusinessAffected.flatMap(
            (solution: string) => businessSolutionServices[solution] || []
          );
          setDynamicApplications(applications);

          return {
            ...prev,
            businessAffected: updatedBusinessAffected,
            affectedApplications: [],
          };
        });
        return;
      }

      // Handle checkbox changes for array fields
      if (e.target instanceof HTMLInputElement && e.target.type === "checkbox") {
        const checked = e.target.checked;
        
        setFormData((prev) => {
          const arrayFields: (keyof ChangeFormData)[] = [
            "affectedBrands",
            "modGenerations",
            "regions",
            "affectedServices",
            "affectedApplications",
          ];

          if (arrayFields.includes(name)) {
            const prevValues = prev[name] as string[];
            return {
              ...prev,
              [name]: checked
                ? [...prevValues, value]
                : prevValues.filter((item) => item !== value),
            };
          }
          return prev;
        });
        return;
      }

      // Handle all other changes
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        eventType:
          name === "customerImpact"
            ? value === "No"
              ? "Standard Change"
              : "Emergency Change"
            : name === "hotfix"
            ? value === "Yes"
              ? "Emergency Change"
              : "Risky Change (Normal)"
            : prev.eventType,
      }));
    },
    [businessSolutionServices, setFormData]
  );
  
  // Validate form data
  const validateForm = () => {
    const missingFields = [];
  
    if (!formData.customerImpact) missingFields.push("Customer Impact");
    if (!formData.status) missingFields.push("Status");
    if (!formData.businessSolution) missingFields.push("Business Solution");
    if (formData.affectedServices.length === 0) missingFields.push("Affected Services");
    if (formData.businessAffected.length === 0) missingFields.push("Business Affected");
    if (formData.affectedApplications.length === 0) missingFields.push("Affected Applications");
    if (formData.affectedBrands.length === 0) missingFields.push("Affected Brands");
    if (formData.regions.length === 0) missingFields.push("Regions");
    if (formData.ticketLink.length === 0) missingFields.push("Ticket Number");
    if (formData.changeNotes.length === 0) missingFields.push("Change Notes");
    if (!formData.startDeployment) missingFields.push("Start Deployment");
    if (!formData.endDeployment) missingFields.push("End Deployment");
  
    if (missingFields.length > 0) {
      const message = "Please fill out the following required fields:\n\n" + missingFields.map(field => `- ${field}`).join("\n");
      ShowCustomAlert(message);
      return false;
    }
  
    return true;
  };

  const parseDate = (dateString: string): Date => {
    const [day, month, yearTime] = dateString.split('.');
    const [year, time] = yearTime.split(' ');
    const formattedDateString = `${year}-${month}-${day} ${time}:00`; 
    return new Date(formattedDateString);
  };

  // Validate deployment dates
  const validateDeploymentDates = (startDate: string, endDate: string): boolean => {
    const start = parseDate(startDate);
    const end = parseDate(endDate);
    return start < end;
  };

  // Render checkbox group dynamically
  const renderCheckboxGroup = (options: string[], name: string & keyof ChangeFormData) => (
    options.map((option) => (
      <FormControlLabel
        key={option}
        control={
          <Checkbox
            name={name}
            value={option}
            checked={(formData[name] as string[]).includes(option)}
            onChange={handleChange}
          />
        }
        label={option}
      />
    ))
  );

  return (
    <Box sx={{ margin: "auto" }}>
      {/* Cache Prompt */}
      {showCachePrompt && (
        <Box sx={{ bgcolor: "#e3f2fd", border: "1px solid #90caf9", borderRadius: 2, p: 2, mb: 3 }}>
          <Typography sx={{ mb: 2, fontWeight: "bold" }}>
            Found previously saved form data (cached within last 12 hours)
          </Typography>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                cacheControls.loadCache();
                setShowCachePrompt(false);
              }}
            >
              Load Saved Data
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                cacheControls.skipCache();
                setShowCachePrompt(false);
              }}
            >
              Start Fresh
            </Button>
          </Box>
        </Box>
      )}

      {/* Decision Wizard */}
      <Box sx={{ bgcolor: "white", boxShadow: 3, borderRadius: 2, p: 3, mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2, textAlign: "center" }}>
          Decision Wizard
        </Typography>

        {/* Customer Impact */}
        <FormControl component="fieldset" sx={{ display: "block", mb: 2 }}>
          <FormLabel component="legend">Is there Customer Impact? <span style={{ color: "red", marginRight: 4 }}>*</span></FormLabel>
          <RadioGroup row name="customerImpact" value={formData.customerImpact} onChange={handleChange}>
            <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
            <FormControlLabel value="No" control={<Radio />} label="No" />
          </RadioGroup>
        </FormControl>

        {/* Hotfix - Appears only if Customer Impact is Yes */}
        {formData.customerImpact === "Yes" && (
          <FormControl component="fieldset" sx={{ display: "block", mt: 2 }}>
            <FormLabel component="legend">Is this change a fix for an incident? (hotfix)<span style={{ color: "red", marginRight: 4 }}>*</span></FormLabel>
            <RadioGroup row name="hotfix" value={formData.hotfix} onChange={handleChange}>
              <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
              <FormControlLabel value="No" control={<Radio />} label="No" />
            </RadioGroup>
          </FormControl>
        )}
      </Box>

      {/* Create Announcement */}
      <Box sx={{ bgcolor: "white", boxShadow: 3, borderRadius: 2, p: 3, mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2, textAlign: "center" }}>
          Create Announcement
        </Typography>
        {/* Status Selection Buttons */}
        <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mb: 3 }}>
          <Button
            sx={{
              backgroundColor: activeStatus === "Initial" ? "#cc0000" : "#ffffff",
              color: activeStatus === "Initial" ? "#ffffff" : "#ff0000",
              border: "1px solid #ff0000",
              "&:hover": { backgroundColor: "#cc0000", color: "#ffffff" },
            }}
            onClick={() => handleStatusChange("Initial")}
          >
            Initial
          </Button>
          <Button
            sx={{
              backgroundColor: activeStatus === "Update" ? "#d4aa00" : "#ffffff",
              color: activeStatus === "Update" ? "#ffffff" : "#ffcc00",
              border: "1px solid #ffcc00",
              "&:hover": { backgroundColor: "#d4aa00", color: "#ffffff" },
            }}
            onClick={() => handleStatusChange("Update")}
          >
            Update
          </Button>
          <Button
            sx={{
              backgroundColor: activeStatus === "Done" ? "#006400" : "#ffffff",
              color: activeStatus === "Done" ? "#ffffff" : "#008000",
              border: "1px solid #008800",
              "&:hover": { backgroundColor: "#006400", color: "#ffffff" },
            }}
            onClick={() => handleStatusChange("Done")}
          >
            Done
          </Button>
        </Box>
        {/* Business Solution Dropdown */}
        <FormControl fullWidth sx={{ mb: 3 }}>
          <Typography sx={{ mb: 1 }}>
            Which Business Solution is going to change? <span style={{ color: "red", marginRight: 4 }}>*</span>
          </Typography>
          <Select name="businessSolution" value={formData.businessSolution} onChange={handleBusinessSolutionChange}>
            <MenuItem value="">Select...</MenuItem>
            {businessSolutions.map((solution) => (
              <MenuItem key={solution} value={solution}>
                {solution}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          {/* Dynamic First Checkbox */}
          {formData.businessSolution && (
            <Box sx={{ flex: 1 }}>
              <FormControl component="fieldset" sx={{ mb: 3 }}>
                <Typography component="legend">Which Application/Service is going to change? <span style={{ color: "red", marginRight: 4 }}>*</span></Typography>
                <FormGroup>
                  {dynamicAffectedServices.map((option: string) => (
                    <FormControlLabel
                      key={option}
                      control={
                        <Checkbox
                          name="affectedServices"
                          value={option}
                          checked={formData.affectedServices.includes(option)}
                          onChange={handleChange}
                        />
                      }
                      label={option}
                    />
                  ))}
                </FormGroup>
              </FormControl>
            </Box>
          )}
        </Box>
      </Box>

      <Box sx={{ bgcolor: "white", boxShadow: 3, borderRadius: 2, p: 3, mb: 3 }}>
        <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap", mt: 3 }}>
          {/* Business Solutions that will be affected */}
          <Box sx={{ flex: 1 }}>
            <FormControl component="fieldset" sx={{ mb: 3 }}>
              <Typography component="legend" sx={{ mb: 1 }}>
                Which Business Solution(s) will be Affected? <span style={{ color: "red", marginRight: 4 }}>*</span>
              </Typography>
              <Box sx={{ maxHeight: 200, overflow: "auto", border: "1px solid #ccc", borderRadius: 1, padding: 1 }}>
                <FormGroup>
                  {businessSolutions.map((solution) => (
                    <FormControlLabel
                      key={solution}
                      control={
                        <Checkbox
                          name="businessAffected"
                          value={solution}
                          checked={formData.businessAffected.includes(solution)}
                          onChange={handleChange}
                        />
                      }
                      label={solution}
                    />
                  ))}
                </FormGroup>
              </Box>
            </FormControl>
          </Box>
          {/* Dynamic Applications based on affected Business Solutions */}
          <Box sx={{ flex: 1 }}>
            <FormControl component="fieldset" sx={{ mb: 3 }}>
              <Typography component="legend">
                Which Application(s) will be Affected? <span style={{ color: "red", marginRight: 4 }}>*</span>
              </Typography>
              <Box sx={{ maxHeight: 200, overflow: "auto", border: "1px solid #ccc", borderRadius: 1, padding: 1 }}>
                <FormGroup>
                  {dynamicApplications.map((option) => (
                    <FormControlLabel
                      key={option}
                      control={
                        <Checkbox
                          name="affectedApplications"
                          value={option}
                          checked={formData.affectedApplications.includes(option)}
                          onChange={handleChange}
                        />
                      }
                      label={option}
                    />
                  ))}
                </FormGroup>
              </Box>
            </FormControl>
          </Box>
        </Box>
      </Box>

      <Box sx={{ bgcolor: "white", boxShadow: 3, borderRadius: 2, p: 3 }}>
        {/* Checkbox Groups */}
        <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          <Box sx={{ flex: 1 }}>
            <FormControl component="fieldset" sx={{ mb: 3 }}>
              <FormLabel component="legend">Affected Brands <span style={{ color: "red", marginRight: 4 }}>*</span></FormLabel>
              <FormGroup>
                {renderCheckboxGroup(["Volkswagen", "Seat", "Cupra", "Skoda", "Audi", "Bentley", "Porsche", "Lamborghini"], "affectedBrands")}
              </FormGroup>
            </FormControl>
          </Box>
          <Box sx={{ flex: 1 }}>
            <FormControl component="fieldset" sx={{ mb: 3 }}>
              <FormLabel component="legend">MOD Generations</FormLabel>
              <FormGroup>{renderCheckboxGroup(["MOD1", "MOD2", "MOD3", "MOD4"], "modGenerations")}</FormGroup>
            </FormControl>
          </Box>
          <Box sx={{ flex: 1 }}>
            <FormControl component="fieldset" sx={{ mb: 3 }}>
              <FormLabel component="legend">Regions <span style={{ color: "red", marginRight: 4 }}>*</span></FormLabel>
              <FormGroup>{renderCheckboxGroup(["EMEA", "EU", "DE", "NAR", "AP", "CHN", "RoW(Rest of World)"], "regions")}</FormGroup>
            </FormControl>
          </Box>
        </Box>

        {/* Text input fields */}
        <TextField fullWidth label="Affected Instance(s)" name="instances" value={formData.instances} onChange={handleChange} multiline minRows={2} sx={{ mb: 3 }} />
        <TextField fullWidth label="Downtime & Impact on Users" name="downtimeImpact" value={formData.downtimeImpact} onChange={handleChange} multiline minRows={2} sx={{ mb: 3 }} />
        <TextField fullWidth label="Actions Needed from Developers" name="developerActions" value={formData.developerActions} onChange={handleChange} multiline minRows={2} sx={{ mb: 3 }} />
        <TextField fullWidth label="Actions Needed from Consumers" name="consumerActions" value={formData.consumerActions} onChange={handleChange} multiline minRows={2} sx={{ mb: 3 }} />
        <TextField
          fullWidth
          label={<span>Changed ID/SC3 Number <span style={{ color: "red", marginRight: 4 }}>*</span></span>}
          name="ticketLink"
          value={formData.ticketLink}
          onChange={handleChange}
          multiline
          minRows={2}
          sx={{ mb: 3 }}
        />
        <TextField
          fullWidth
          label={<span>Change Notes <span style={{ color: "red", marginRight: 4 }}>*</span></span>}
          name="changeNotes"
          value={formData.changeNotes}
          onChange={handleChange}
          multiline
          minRows={2}
          sx={{ mb: 3 }}
        />

        <Typography sx={{ flex: 1, fontWeight: 'bold', textAlign: 'center', mb: 3 }}>
          Please only use German Time. Time now {berlinTime} ({gmtOffset})
        </Typography>

        {/* Deployment Date Pickers */}
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>
          {/* Start of Deployment */}
          <FormControl sx={{ flex: 1 }}>
            <FormLabel sx={{ mb: 1 }}>Start of Deployment <span style={{ color: "red", marginRight: 4 }}>*</span></FormLabel>
            <Flatpickr
              options={{
                enableTime: true,
                time_24hr: true,
                dateFormat: "d.m.Y H:i",
                minDate: "today",
              }}
              value={formData.startDeployment}
              onChange={([date]: Date[]) => {
                const newStartDate = date.toLocaleString("sv-SE", { timeZone: "Europe/Tallinn" });
                const formattedDate = newStartDate.split(" ")[0].split("-").reverse().join(".");
                const newValue = `${formattedDate} ${newStartDate.split(" ")[1]}`;

                setFormData((prev) => ({ ...prev, startDeployment: newValue }));

                if (formData.endDeployment) {
                  if (!validateDeploymentDates(newValue, formData.endDeployment)) {
                    ShowCustomAlert("Start date must be before end date!");
                    setFormData((prev) => ({ ...prev, startDeployment: "" }));
                  }
                }
              }}
              style={{
                width: "100%",
                padding: "10px",
                borderRadius: "4px",
                border: "1px solid #ccc",
                fontSize: "16px",
              }}
            />
          </FormControl>

          {/* End of Deployment */}
          <FormControl sx={{ flex: 1 }}>
            <FormLabel sx={{ mb: 1 }}>End of Deployment <span style={{ color: "red", marginRight: 4 }}>*</span></FormLabel>
            <Flatpickr
              options={{
                enableTime: true,
                time_24hr: true,
                dateFormat: "d.m.Y H:i",
                minDate: "today",
              }}
              value={formData.endDeployment}
              onChange={([date]: Date[]) => {
                const newEndDate = date.toLocaleString("sv-SE", { timeZone: "Europe/Tallinn" });
                const formattedDate = newEndDate.split(" ")[0].split("-").reverse().join(".");
                const newValue = `${formattedDate} ${newEndDate.split(" ")[1]}`;

                if (formData.startDeployment && !validateDeploymentDates(formData.startDeployment, newValue)) {
                  ShowCustomAlert("End date must be after start date!");
                  setFormData((prev) => ({ ...prev, endDeployment: "" }));
                } else {
                  setFormData((prev) => ({ ...prev, endDeployment: newValue }));
                }
              }}
              style={{
                width: "100%",
                padding: "10px",
                borderRadius: "4px",
                border: "1px solid #ccc",
                fontSize: "16px",
              }}
            />
          </FormControl>
        </Box>

        {/* Show Preview Button */}
        <Button
          variant="contained"
          color="primary"
          fullWidth
          sx={{ mb: 3 }}
          onClick={(e) => {
            e.preventDefault();
            if (validateForm()) setShowPreview(!showPreview);
          }}
        >
          {showPreview ? "Hide" : "Show"} Preview
        </Button>

        {showPreview && (
          <Box sx={{ mb: 3 }}>
            <AnnouncementPreview formData={formData} />

            <Button
              variant="contained"
              color="primary"
              fullWidth
              sx={{ mt: 3 }}
              onClick={async (e) => {
                e.preventDefault();
                setLoading(true);
                try {
                  if (validateForm()) {
                    await sendEmail(
                      formData.businessSolution + "," + formData.businessAffected,
                      "Change Announcement - " + formData.status + " - " + formData.ticketLink,
                      <AnnouncementPreview formData={formData} />,
                      csrfToken
                    );
                    ShowCustomAlert("Email sent successfully!");
                  }
                } catch {
                  ShowCustomAlert("Failed to send email. Please try again.");
                } finally {
                  setLoading(false);
                }
              }}
              disabled={loading}
            >
              {loading ? "Sending..." : "Send"}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AnnouncementForm;
